import React, { useEffect, useMemo, useState } from 'react'
import { useParams, NavLink } from 'react-router-dom'
import {
  fetchProductById,
  fetchProducts,
  formatProductPrice,
  getImageUrl,
} from '../../api/productApi'
import RelatedProducts from '../Sliders/RelatedProducts'
import SupplierCard from '../UI/SupplierCard'
import fallbackProductImage from '../../assets/images/FortuneBasket.png'

const ShopDetail = () => {
  const params = useParams()
  const productId = params?.id

  const [product, setProduct] = useState(null)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [expandBenefits, setExpandBenefits] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function loadProduct() {
      if (!productId) {
        setLoading(false)
        setError('Product not found.')
        return
      }

      setLoading(true)
      setError('')
      setActiveIndex(0)
      setExpandBenefits(false)

      try {
        const [productData, allProducts] = await Promise.all([
          fetchProductById(productId),
          fetchProducts(),
        ])

        if (cancelled) return

        setProduct(productData)
        setRelatedProducts(
          allProducts.filter((item) => item.product_id !== productData.product_id)
        )
      } catch (err) {
        if (!cancelled) {
          setProduct(null)
          setRelatedProducts([])
          setError(err.message || 'Product not found.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadProduct()
    return () => {
      cancelled = true
    }
  }, [productId])

  const galleryImages = useMemo(() => {
    if (!product?.product_images?.length) return []
    return product.product_images.map((img) => getImageUrl(img)).filter(Boolean)
  }, [product])

  const sellingPrice = product?.discount_price ?? product?.price
  const hasValidPrice =
    sellingPrice != null &&
    sellingPrice !== '' &&
    Number.isFinite(Number(sellingPrice))
  const hasDiscount =
    hasValidPrice &&
    product?.discount_price != null &&
    Number(product.discount_price) < Number(product.price)

  const shortDescription = product?.short_description || ''
  const benefits = product?.benefits || []
  const benefitsTitle =
    product?.benefits_title || (benefits.length ? 'Benefits' : '')
  const longDescription = product?.long_description || ''
  const providers = product?.providers || []
  const showLongSection = benefits.length > 0 || Boolean(longDescription)

  if (loading) {
    return (
      <section className="space-top">
        <div className="container">
          <p className="text-center py-5">Loading product...</p>
        </div>
      </section>
    )
  }

  if (!product || error) {
    return (
      <section className="space-top">
        <div className="container">
          <p className="text-center py-5">{error || 'Product not found.'}</p>
          <div className="text-center">
            <NavLink className="second-btn text-center text-lora" to="/shop">
              Back to Shop
            </NavLink>
          </div>
        </div>
      </section>
    )
  }

  const discountBadge =
    hasDiscount && Number(product.discount_percentage) > 0 ? (
      <span className="shop-detail-discount-badge">
        {product.discount_percentage}% OFF
      </span>
    ) : null

  return (
    <>
      <section className="space-top">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <div className="p-3 rounded-xl second-bg shop-detail-gallery">
                <div className="rounded-xl overflow-hidden bg-white shop-detail-main-image">
                  {galleryImages.length > 0 ? (
                    <img
                      src={galleryImages[activeIndex]}
                      alt={product.product_name}
                      className="w-100 object-cover"
                      onError={(event) => {
                        event.currentTarget.onerror = null
                        event.currentTarget.src = fallbackProductImage
                      }}
                    />
                  ) : (
                    <img
                      src={fallbackProductImage}
                      alt={product.product_name}
                      className="w-100 object-cover"
                    />
                  )}
                </div>
                {galleryImages.length > 1 && (
                  <div className="d-flex gap-2 mt-3 shop-detail-thumbs">
                    {galleryImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`shop-detail-thumb ${
                          activeIndex === idx ? 'shop-detail-thumb--active' : ''
                        }`}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`Select image ${idx + 1}`}
                      >
                        <img
                          src={img}
                          alt={`thumb-${idx}`}
                          className="object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-6 col-12">
              <div className="shop-detail-info d-flex flex-column h-100">
                <h3 className="text-lora font-semibold mb-3 lg:text-[36px]">
                  {product.product_name}
                </h3>

                <div className="shop-detail-price-row d-flex align-items-baseline gap-2 mb-3">
                  <div className="shop-detail-price">
                    {hasValidPrice
                      ? `₹${formatProductPrice(sellingPrice)}`
                      : '-'}
                  </div>
                  {hasDiscount && (
                    <div className="shop-detail-original-price">
                      ₹{formatProductPrice(product.price)}
                    </div>
                  )}
                  {discountBadge}
                </div>

                {shortDescription && (
                  <div className="shop-detail-short-box mb-4">
                    <p className="mb-0">{shortDescription}</p>
                  </div>
                )}

                {showLongSection && (
                  <div className="shop-detail-long-block mb-4">
                    <h6 className="shop-detail-benefits-title mb-3">
                      {benefitsTitle || `About ${product.product_name}`}
                    </h6>
                    <div className="shop-detail-benefits-box">
                      {benefits.length > 0 ? (
                        <>
                          <ul
                            className={`shop-detail-benefits-list ${
                              expandBenefits ? 'is-expanded' : 'is-collapsed'
                            }`}
                          >
                            {(expandBenefits ? benefits : benefits.slice(0, 1)).map(
                              (benefit) => (
                                <li key={benefit}>{benefit}</li>
                              )
                            )}
                          </ul>
                          {benefits.length > 1 && (
                            <button
                              type="button"
                              className="shop-detail-more-btn"
                              onClick={() => setExpandBenefits((prev) => !prev)}
                            >
                              {expandBenefits ? 'Less' : 'More'}
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <p
                            className={`shop-detail-long-text mb-0 shop-detail-clamp-text ${
                              expandBenefits ? 'is-expanded' : ''
                            }`}
                          >
                            {longDescription}
                          </p>
                          {longDescription.length > 80 && (
                            <button
                              type="button"
                              className="shop-detail-more-btn"
                              onClick={() => setExpandBenefits((prev) => !prev)}
                            >
                              {expandBenefits ? 'Less' : 'More'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {product.quantity > 0 && (
                  <div className="shop-detail-stock-wrap">
                    <div className="shop-detail-stock">
                      Last {product.quantity} piece
                      {product.quantity === 1 ? '' : 's'} left
                    </div>
                  </div>
                )}

                <div className="shop-detail-actions d-flex justify-content-between align-items-center gap-3 mt-auto">
                  <NavLink
                    to="/order-page"
                    className="first-btn shop-detail-order-btn text-center"
                    state={product}
                  >
                    Order Now
                  </NavLink>
                  <NavLink
                    className="second-btn text-center text-lora shop-detail-back-btn"
                    to="/shop"
                  >
                    Back to Shop
                  </NavLink>
                </div>
              </div>
            </div>
          </div>

          {providers.length > 0 && (
            <div className="shop-detail-providers mt-5">
              <h5 className="shop-detail-providers-title text-lora mb-3">
                Suppliers / Service Providers
              </h5>
              <div className="row g-3 flex-nowrap overflow-auto fortune-scroll-row pb-2">
                {providers.map((provider) => (
                  <div
                    key={provider.id}
                    className="col-10 col-sm-6 col-md-4 col-lg-3"
                  >
                    <SupplierCard provider={provider} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {relatedProducts.length > 0 && (
        <section className="space-top">
          <div className="container">
            <h5 className="lg:text-[24px] text-gray-900 font-semibold mb-3 text-lora">
              You May Also Like
            </h5>
            <div className="row">
              <RelatedProducts relatedProductsData={relatedProducts} />
            </div>
          </div>
        </section>
      )}
    </>
  )
}

export default ShopDetail
